'use client';

import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';

interface Props {
  years: number[];
  medianPrices: number[];
  yearlyPct: number[];
  district: string;
  propertyTypeLabel: string;
  askingPrice?: number;
}

function formatGBP(v: number): string {
  if (v >= 1_000_000) return `£${(v / 1_000_000).toFixed(1)}m`;
  if (v >= 1_000) return `£${Math.round(v / 1_000)}k`;
  return `£${v}`;
}

export default function PriceHistoryChart({
  years,
  medianPrices,
  yearlyPct,
  district,
  propertyTypeLabel,
  askingPrice,
}: Props) {
  const data = years.map((year, i) => ({
    year,
    price: medianPrices[i],
    pct: yearlyPct[i],
  }));

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <h4 className="text-sm font-semibold text-[#3a3935]">
          {propertyTypeLabel} in {district} — Price History
        </h4>
        <span className="text-xs text-[#7a7870]">
          {years[0]}–{years[years.length - 1]}
        </span>
      </div>

      <div className="h-56 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e8e4dc" />
            <XAxis
              dataKey="year"
              tick={{ fontSize: 11, fill: '#7a7870' }}
              tickLine={false}
              axisLine={{ stroke: 'rgba(0,0,0,0.1)' }}
              interval={Math.max(0, Math.floor(data.length / 8))}
            />
            <YAxis
              yAxisId="price"
              tickFormatter={formatGBP}
              tick={{ fontSize: 11, fill: '#7a7870' }}
              tickLine={false}
              axisLine={false}
              width={55}
            />
            <YAxis
              yAxisId="pct"
              orientation="right"
              tickFormatter={(v) => `${Number(v) > 0 ? '+' : ''}${Number(v).toFixed(0)}%`}
              tick={{ fontSize: 10, fill: '#b8b5ae' }}
              tickLine={false}
              axisLine={false}
              width={40}
            />
            <Tooltip
              contentStyle={{
                fontSize: 12,
                borderRadius: 8,
                border: '1px solid rgba(0,0,0,0.1)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
              formatter={(value, name) => {
                const v = Number(value);
                if (String(name) === 'price') return [formatGBP(v), 'Median price'];
                return [`${v > 0 ? '+' : ''}${v.toFixed(1)}%`, 'YoY change'];
              }}
              labelFormatter={(label) => `Year: ${label}`}
            />

            {/* Percentage bars behind the line */}
            <Bar
              yAxisId="pct"
              dataKey="pct"
              fill="#e8e4dc"
              radius={[2, 2, 0, 0]}
              opacity={0.6}
            />

            {/* Zero line for percentages */}
            <ReferenceLine yAxisId="pct" y={0} stroke="#d4d0c8" strokeDasharray="3 3" />

            {/* Price line */}
            <Line
              yAxisId="price"
              type="monotone"
              dataKey="price"
              stroke="#1a4a3a"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: '#2d7a5f' }}
            />

            {/* Asking price reference line */}
            {askingPrice && (
              <ReferenceLine
                yAxisId="price"
                y={askingPrice}
                stroke="#f59e0b"
                strokeDasharray="6 3"
                label={{
                  value: `Asking: ${formatGBP(askingPrice)}`,
                  position: 'right',
                  fontSize: 10,
                  fill: '#f59e0b',
                }}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
